/**
 * Session-scoped gate for the unified opening notice.
 *
 * The authored score can be replaced or re-mounted while a listening session
 * continues. Event-key sets are intentionally rebuilt during those refreshes,
 * so the welcome needs an independent lifetime guard.
 */
export function isSessionWelcomeEvent(event = {}) {
  return String(event?.role || "") === "session_welcome"
    || String(event?.event_id || "") === "session-welcome";
}

export function isSessionWelcomeTrack(track = {}) {
  const category = String(track?.category || "").trim().toLowerCase();
  const file = String(track?.file || track?.audio_file || "").trim().toLowerCase();
  const tags = Array.isArray(track?.tags)
    ? track.tags.map((tag) => String(tag || "").trim().toLowerCase())
    : [];
  return category === "session_welcome"
    || tags.includes("session_welcome")
    || file.startsWith("session_welcome__")
    || file.includes("/session_welcome__");
}

/**
 * Keep the singleton opener out of the generic continuous queue after it has
 * started or been skipped. This matters when a background library refresh
 * replaces the queue while the listener is already playing.
 */
export function selectContinuousMainVoiceQueue(queue = [], welcomeStatus = "idle") {
  const items = Array.isArray(queue) ? queue : [];
  if (welcomeStatus === "idle") return items;
  return items.filter((track) => !isSessionWelcomeTrack(track));
}

export function createSessionWelcomePlaybackGuard() {
  let state = "idle";
  return {
    claim(event) {
      if (!isSessionWelcomeEvent(event)) return "not_welcome";
      if (state === "idle") {
        state = "starting";
        return "claimed";
      }
      return state;
    },
    settle(event, started) {
      if (!isSessionWelcomeEvent(event)) return;
      state = started ? "played" : "idle";
    },
    markPlayed(event) {
      if (isSessionWelcomeEvent(event)) state = "played";
    },
    reset() {
      state = "idle";
    },
    status() {
      return state;
    },
  };
}
